/* =============================================================================
   admin.jsx  —  host panel. Token sign-in, settings, roster, keepsakes,
   gift tracker, QR + CSV/print exports.

   Auth: the host pastes the shared admin token once. It is kept in
   sessionStorage and sent as `Authorization: Bearer <token>` by the DB layer.
   The Worker validates it (constant-time). "Sign out" clears it.
============================================================================= */
import { useState, useEffect } from 'react';
import { useAsyncData, Loading, ErrorBlock, Field, Input } from './ui';
import { Ladybug } from './decor';
import { fmtDateTime, downloadCSV, qrSvg } from '../lib/helpers';
import { DB, setAdminToken, clearAdminToken, hasAdminToken } from '../lib/api';
import {
  SettingsEditor,
  TogglesEditor,
  RegistryEditor,
  FAQEditor,
  ScheduleEditor,
  VirtualEditor,
  GalleryEditor,
} from './admin-editors';
import { EmailsTab } from './admin-emails';

const NONE = <span className="none-tag">NONE</span>;
const orNone = (v) => (v === '' || v == null ? NONE : v);

/* ------------------------------- gate ------------------------------------ */
// Verifies the token by attempting an admin read. Valid -> onPass; else clear.
function Gate({ onPass }) {
  const [token, setToken] = useState('');
  const [err, setErr] = useState(false);
  const [checking, setChecking] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (checking || !token.trim()) {
      if (!token.trim()) setErr(true);
      return;
    }
    setChecking(true);
    setErr(false);
    setAdminToken(token.trim());
    try {
      await DB.getRSVPs(); // 200 => valid token; 401 => invalid
      onPass();
    } catch {
      clearAdminToken();
      setErr(true);
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="gate">
      <form className="card" onSubmit={submit}>
        <div style={{ marginBottom: 10 }}><Ladybug size={46} /></div>
        <h2 style={{ fontSize: '1.8rem' }}>Host sign in</h2>
        <p className="muted" style={{ fontSize: '0.9rem' }}>This area is for Kenna and Anthony.</p>
        <Field error={err ? 'That token does not match. Try again.' : null}>
          <Input type="password" value={token} onChange={(v) => { setToken(v); setErr(false); }} placeholder="Host token" invalid={err} autoComplete="off" />
        </Field>
        <button className="btn btn-primary btn-block" type="submit" disabled={checking}>
          {checking ? 'Checking...' : 'Enter'}
        </button>
        <p className="faint" style={{ fontSize: '0.74rem', marginTop: 14, marginBottom: 0 }}>
          Enter the host token shared with you. It is kept only for this browser session.
        </p>
        <p style={{ marginTop: 18, marginBottom: 0 }}><a href="#top" style={{ fontSize: '0.85rem' }}>Back to the invitation</a></p>
      </form>
    </div>
  );
}

/* ------------------------------- roster ---------------------------------- */
function RosterTab() {
  const [key, setKey] = useState(0);
  const { loading, error, data, reload } = useAsyncData(() => DB.getRSVPs(), [key]);
  useEffect(() => {
    const h = () => setKey((k) => k + 1);
    window.addEventListener('llb:data-changed', h);
    return () => window.removeEventListener('llb:data-changed', h);
  }, []);

  if (loading) return <Loading label="Loading the roster" />;
  if (error) return <ErrorBlock error={error} onRetry={reload} />;

  const rows = [...data].sort((a, b) => b.timestamp - a.timestamp);
  const accepting = rows.filter((r) => r.attending === 'accept');
  const declining = rows.filter((r) => r.attending === 'decline');
  const head = accepting.reduce((s, r) => s + (parseInt(r.partySize, 10) || 0), 0);

  const csvHeaders = [
    { label: 'Submitted', get: (r) => fmtDateTime(r.timestamp) },
    { label: 'First name', key: 'firstName' },
    { label: 'Last name', key: 'lastName' },
    { label: 'Email', key: 'email' },
    { label: 'Attending', get: (r) => (r.attending === 'accept' ? 'Accept' : 'Decline') },
    { label: 'Party size', get: (r) => r.attending === 'accept' ? r.partySize : '' },
    { label: 'Party names', key: 'partyNames' },
    { label: 'Street', key: 'street' }, { label: 'Apt', key: 'apt' },
    { label: 'City', key: 'city' }, { label: 'State', key: 'state' }, { label: 'ZIP', key: 'zip' },
    { label: 'Chose gift', get: (r) => (r.gotGift ? 'Yes' : 'No') },
    { label: 'Note', key: 'note' },
    { label: 'Privacy ack', get: (r) => (r.privacyAck === true ? 'TRUE' : 'FALSE') },
  ];

  return (
    <div>
      <div className="stat-grid">
        <div className="stat"><div className="n">{accepting.length}</div><div className="l">Joyfully accepting</div></div>
        <div className="stat"><div className="n">{declining.length}</div><div className="l">Regretfully declining</div></div>
        <div className="stat"><div className="n">{head}</div><div className="l">Total head count</div></div>
      </div>

      <div className="row no-print" style={{ marginBottom: 16, justifyContent: 'space-between' }}>
        <h2 style={{ fontSize: '1.6rem', color: 'var(--terra-deep)' }}>Guest roster</h2>
        <div className="row">
          <button className="btn btn-secondary btn-sm" onClick={() => window.print()}>Print roster</button>
          <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV('rsvps.csv', rows, csvHeaders)} disabled={!rows.length}>Export CSV</button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="admin-card center muted">No RSVPs yet. They will appear here, newest first.</div>
      ) : (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Submitted</th><th>Name</th><th>Email</th><th>Attending</th>
                <th>Party</th><th>Others</th><th>Address</th><th>Gift</th><th>Note</th><th>Privacy</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const addr = r.attending === 'accept'
                  ? [r.street, r.apt, [r.city, r.state].filter(Boolean).join(', '), r.zip].filter(Boolean).join(' ')
                  : '';
                return (
                  <tr key={r.id}>
                    <td>{fmtDateTime(r.timestamp)}</td>
                    <td>{r.firstName} {r.lastName}</td>
                    <td>{r.email}</td>
                    <td>{r.attending === 'accept' ? <span className="pill">Accept</span> : <span style={{ color: 'var(--ink-faint)' }}>Decline</span>}</td>
                    <td>{r.attending === 'accept' ? r.partySize : NONE}</td>
                    <td className="wrap-cell">{orNone(r.partyNames)}</td>
                    <td className="wrap-cell">{orNone(addr)}</td>
                    <td>{r.attending === 'accept' ? (r.gotGift ? 'Yes' : 'No') : NONE}</td>
                    <td className="wrap-cell">{orNone(r.note)}</td>
                    <td>{r.privacyAck === true ? 'TRUE' : 'FALSE'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ keepsakes -------------------------------- */
function KeepsakesTab() {
  const [key, setKey] = useState(0);
  const wishes = useAsyncData(() => DB.getWishes(), [key]);
  const preds = useAsyncData(() => DB.getPredictions(), [key]);
  useEffect(() => {
    const h = () => setKey((k) => k + 1);
    window.addEventListener('llb:data-changed', h);
    return () => window.removeEventListener('llb:data-changed', h);
  }, []);

  const wishRows = wishes.data ? [...wishes.data].sort((a, b) => b.timestamp - a.timestamp) : [];
  const predRows = preds.data ? [...preds.data].sort((a, b) => b.timestamp - a.timestamp) : [];

  return (
    <div>
      <div className="admin-card">
        <div className="row no-print" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Well wishes &amp; advice</h3>
          <button className="btn btn-ghost btn-sm" disabled={!wishRows.length}
            onClick={() => downloadCSV('well-wishes.csv', wishRows, [
              { label: 'Submitted', get: (r) => fmtDateTime(r.timestamp) },
              { label: 'Name', key: 'name' }, { label: 'Email', key: 'email' }, { label: 'Message', key: 'message' },
            ])}>Export CSV</button>
        </div>
        {wishes.loading ? <Loading /> : wishes.error ? <ErrorBlock error={wishes.error} onRetry={wishes.reload} /> :
          wishRows.length === 0 ? <p className="muted">No wishes yet.</p> : (
            <div className="table-scroll" style={{ marginTop: 12 }}>
              <table className="data">
                <thead><tr><th>Submitted</th><th>Name</th><th>Email</th><th>Message</th></tr></thead>
                <tbody>{wishRows.map((w) => (
                  <tr key={w.id}><td>{fmtDateTime(w.timestamp)}</td><td>{w.name}</td><td>{orNone(w.email)}</td><td className="wrap-cell">{w.message}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
      </div>

      <div className="admin-card">
        <div className="row no-print" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Baby predictions</h3>
          <button className="btn btn-ghost btn-sm" disabled={!predRows.length}
            onClick={() => downloadCSV('predictions.csv', predRows, [
              { label: 'Submitted', get: (r) => fmtDateTime(r.timestamp) },
              { label: 'Name', key: 'name' }, { label: 'Email', key: 'email' },
              { label: 'Birth date', key: 'birthDate' }, { label: 'Weight', key: 'weight' }, { label: 'Length', key: 'length' },
              { label: 'Hair', key: 'hairColor' }, { label: 'Eyes', key: 'eyeColor' },
              { label: 'Takes after', key: 'takesAfter' }, { label: 'Name guess', key: 'nameGuess' },
            ])}>Export CSV</button>
        </div>
        {preds.loading ? <Loading /> : preds.error ? <ErrorBlock error={preds.error} onRetry={preds.reload} /> :
          predRows.length === 0 ? <p className="muted">No predictions yet.</p> : (
            <div className="table-scroll" style={{ marginTop: 12 }}>
              <table className="data">
                <thead><tr><th>Name</th><th>Birth date</th><th>Weight</th><th>Length</th><th>Hair</th><th>Eyes</th><th>Takes after</th><th>Name guess</th></tr></thead>
                <tbody>{predRows.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td><td>{orNone(p.birthDate)}</td><td>{orNone(p.weight)}</td><td>{orNone(p.length)}</td>
                    <td>{orNone(p.hairColor)}</td><td>{orNone(p.eyeColor)}</td><td>{orNone(p.takesAfter)}</td><td>{orNone(p.nameGuess)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}

/* ----------------------------- gift tracker ------------------------------ */
function GiftTrackerTab() {
  const { loading, error, data, reload } = useAsyncData(() => DB.getGiftLog(), []);
  const [entries, setEntries] = useState([]);
  useEffect(() => { if (data) setEntries(data); }, [data]);

  async function persist(next) {
    setEntries(next);
    try { await DB.saveGiftLog(next); } catch (e) { console.error(e); }
  }
  const add = () => persist([...entries, { id: makeId('gift'), name: '', gift: '', thanked: false, date: new Date().toISOString().slice(0, 10) }]);
  const edit = (i, patch) => persist(entries.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const del = (i) => persist(entries.filter((_, idx) => idx !== i));

  if (loading) return <Loading />;
  if (error) return <ErrorBlock error={error} onRetry={reload} />;

  const thankedCount = entries.filter((e) => e.thanked).length;

  return (
    <div className="admin-card">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Gift &amp; thank-you tracker</h3>
        <span className="pill">{thankedCount} of {entries.length} thanked</span>
      </div>
      <p className="muted" style={{ marginTop: 6 }}>Private to you. Track who gave what and tick off thank-you cards.</p>
      {entries.length === 0 && <p className="faint">No entries yet. Add one as gifts arrive.</p>}
      {entries.map((g, i) => (
        <div key={g.id} className="list-item" style={{ alignItems: 'center' }}>
          <div className="grow">
            <div className="grid-3">
              <Field label="Guest"><Input value={g.name} onChange={(v) => edit(i, { name: v })} /></Field>
              <Field label="Gift"><Input value={g.gift} onChange={(v) => edit(i, { gift: v })} /></Field>
              <Field label="Date"><Input type="date" value={g.date} onChange={(v) => edit(i, { date: v })} /></Field>
            </div>
            <label className="check" style={{ marginTop: 2 }}>
              <input type="checkbox" checked={g.thanked} onChange={(e) => edit(i, { thanked: e.target.checked })} />
              <span>Thank-you sent</span>
            </label>
          </div>
          <button className="icon-btn" title="Remove" onClick={() => del(i)}>&times;</button>
        </div>
      ))}
      <button className="btn btn-secondary btn-sm" onClick={add} style={{ marginTop: 6 }}>+ Add entry</button>
    </div>
  );
}

/* -------------------------------- share ---------------------------------- */
function ShareTab() {
  const siteUrl = window.location.origin + window.location.pathname;
  const svg = qrSvg(siteUrl, 5);
  return (
    <div className="admin-card center">
      <h3>Share the invitation</h3>
      <p className="muted">Print this QR on signage or paper invites. It points guests to this page.</p>
      <div className="qr-box" dangerouslySetInnerHTML={{ __html: svg }} style={{ width: 200, height: 200 }} />
      <p className="faint" style={{ fontSize: '0.82rem', wordBreak: 'break-all', maxWidth: 360, margin: '14px auto 0' }}>{siteUrl}</p>
      <div className="row center" style={{ marginTop: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => window.print()}>Print QR</button>
      </div>
    </div>
  );
}

/* ------------------------------- admin app ------------------------------- */
export function AdminApp() {
  const [authed, setAuthed] = useState(false);
  const [verifying, setVerifying] = useState(hasAdminToken());
  const [tab, setTab] = useState('roster');
  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  // If a token is already in sessionStorage, verify it silently on mount.
  useEffect(() => {
    if (!hasAdminToken()) return;
    let live = true;
    DB.getRSVPs()
      .then(() => { if (live) setAuthed(true); })
      .catch(() => { clearAdminToken(); })
      .finally(() => { if (live) setVerifying(false); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!authed) return;
    DB.getSettings().then(setSettings).catch((e) => setLoadErr(e.message));
  }, [authed]);

  const setSection = (key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
    setDirty(true);
  };

  async function save() {
    setSaving(true);
    try {
      await DB.saveSettings(settings);
      setDirty(false);
      window.dispatchEvent(new Event('llb:data-changed'));
    } catch (e) { console.error(e); alert('Could not save. Please try again.'); }
    finally { setSaving(false); }
  }

  function signOut() {
    clearAdminToken();
    setAuthed(false);
    setSettings(null);
    setDirty(false);
  }

  if (!authed) {
    if (verifying) return <div className="admin"><div className="admin-wrap"><Loading label="Opening the host panel" /></div></div>;
    return <Gate onPass={() => { setVerifying(false); setAuthed(true); }} />;
  }
  if (loadErr) return <div className="admin-wrap"><ErrorBlock error={loadErr} /></div>;
  if (!settings) return <div className="admin"><div className="admin-wrap"><Loading label="Opening the host panel" /></div></div>;

  const tabs = [
    ['roster', 'Roster'],
    ['keepsakes', 'Keepsakes'],
    settings.toggles.giftTracker && ['gifts', 'Gifts'],
    ['emails', 'Emails'],
    ['settings', 'Settings'],
    ['content', 'Content'],
    ['share', 'Share'],
  ].filter(Boolean);

  return (
    <div className="admin">
      <div className="admin-bar no-print">
        <h1>Host panel</h1>
        <div className="row" style={{ alignItems: 'center' }}>
          {dirty && <span className="faint" style={{ fontSize: '0.82rem' }}>Unsaved changes</span>}
          <button className="btn btn-primary btn-sm" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
          <a className="btn btn-ghost btn-sm" href="#top">View site</a>
          <button className="btn btn-ghost btn-sm" onClick={signOut}>Sign out</button>
        </div>
      </div>
      <div className="admin-wrap">
        <div className="tabs no-print">
          {tabs.map(([id, label]) => (
            <button key={id} className={`tab ${tab === id ? 'on' : ''}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>

        {tab === 'roster' && <RosterTab />}
        {tab === 'keepsakes' && <KeepsakesTab />}
        {tab === 'gifts' && settings.toggles.giftTracker && <GiftTrackerTab />}
        {tab === 'emails' && <EmailsTab settings={settings} />}
        {tab === 'settings' && (
          <div>
            <SettingsEditor settings={settings} setSection={setSection} />
            <TogglesEditor settings={settings} setSection={setSection} />
          </div>
        )}
        {tab === 'content' && (
          <div>
            <RegistryEditor settings={settings} setSection={setSection} />
            <GalleryEditor onFirstPhoto={() => setSection('toggles', { ...settings.toggles, gallery: true })} />
            <FAQEditor settings={settings} setSection={setSection} />
            <ScheduleEditor settings={settings} setSection={setSection} />
            <VirtualEditor settings={settings} setSection={setSection} />
          </div>
        )}
        {tab === 'share' && <ShareTab />}
      </div>
    </div>
  );
}
